import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock, ArrowRight, CalendarDays } from "lucide-react";
import { DASHBOARD } from "@/constants/testIds";
import usePromoStatus from "@/lib/usePromoStatus";

// Free-tier funnel card: "Here's what your first paid week looks like."
// Shown on Dashboard only when user.tier === "free". Institutional voice —
// concrete daily deliverables, not marketing copy. All strings live in
// i18n so ES/FR members see the same seven-day preview in their language.
const DAY_KEYS = ["day1", "day2", "day3", "day4", "day5", "day6", "day7"];

export default function FirstWeekPreview() {
  const { t } = useTranslation();
  const { params: P } = usePromoStatus();
  return (
    <section
      data-testid={DASHBOARD.firstWeekPreview}
      className="relative overflow-hidden rounded-2xl bg-slate-950 text-white border border-amber-500/25"
    >
      <div className="absolute inset-0 opacity-25 bg-[radial-gradient(circle_at_15%_20%,rgba(30,64,175,0.55),transparent_55%),radial-gradient(circle_at_85%_80%,rgba(245,158,11,0.35),transparent_50%)]" />

      <div className="relative p-8 sm:p-10">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-amber-400 font-semibold">
              <CalendarDays size={14} /> {t("firstWeek.eyebrow")}
            </div>
            <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl tracking-tighter leading-tight">
              {t("firstWeek.title")}
            </h2>
            <p className="mt-3 text-slate-300 max-w-xl text-sm leading-relaxed">
              {t("firstWeek.description")}
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold border border-slate-700 rounded-full px-3 py-1.5 bg-slate-900/40">
            <Lock size={11} /> {t("firstWeek.lockedBadge")}
          </span>
        </div>

        <ol className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {DAY_KEYS.map((k) => (
            <li
              key={k}
              data-testid={DASHBOARD.firstWeekDay}
              className="relative rounded-xl border border-slate-800 bg-slate-900/60 p-4 hover:border-amber-500/40 transition-colors"
            >
              <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400 font-semibold">
                {t(`firstWeek.days.${k}.day`)}
              </div>
              <div className="mt-2 font-display font-semibold text-white text-base leading-snug">
                {t(`firstWeek.days.${k}.title`)}
              </div>
              <div className="mt-2 text-xs text-slate-400 leading-relaxed">
                {t(`firstWeek.days.${k}.body`)}
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-10 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div>
            <div className="font-display font-bold text-lg text-white">
              {t("firstWeek.ctaTitle")}
            </div>
            <div className="mt-1 text-xs text-slate-400">
              {t("firstWeek.ctaSub", P)}
            </div>
          </div>
          <Link
            to="/pricing"
            data-testid={DASHBOARD.firstWeekCta}
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-6 py-3 rounded-md text-sm transition-colors whitespace-nowrap"
          >
            {t("firstWeek.ctaButton")} <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}
